// packages/api/src/PublicCommentService.ts
//
// PUBLIC COMMENTS client (roadmap #6) — the guest-facing read (`publicCommentThread`) + write
// (`publicCommentPost`) for the event website's guest-book and per-item comment threads. Anonymous /
// visitor-identified, published-only, gated by eventsmagic-api's PublicCommentCore. Runs through the
// injected client so it works in the browser (the thread reads on open + posts on submit).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { COMMENT_AVATAR_FIELDS, executeMutationImageSafe, executeQueryImageSafe, imageSafeDocs } from "./answerImageFields";

/** A comment's reactions collapsed for a guest: per emoji, the count and whether this visitor left it. */
export interface PublicCommentReaction {
  reaction: string;
  count: number;
  /** True when the current visitor left this reaction (drives the toggle highlight). */
  mine?: boolean | null;
}

/** A comment as a guest sees it (safe public subset — no account / cookie ids). */
export interface PublicComment {
  id: string;
  /** Parent comment id when this is a reply. */
  channelCommentId?: string | null;
  commenterName?: string | null;
  /** guest | user. */
  commenterKind?: string | null;
  commentText?: string | null;
  hasReplies?: boolean | null;
  createdAt?: string | null;
  reactions?: PublicCommentReaction[] | null;
  /** A2e #91 — the author's stored photo (as the site shows that guest elsewhere); null for none. */
  commenterPhoto?: { id?: string | null; fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  /** A2e #91 — a host's account picture (never a guest's Gravatar on the public site). */
  commenterAvatarUrl?: string | null;
  /** Whether this visitor authored the comment. */
  mine?: boolean | null;
  /** Whether this visitor may change or remove it right now (theirs, and nobody has replied to it). */
  editable?: boolean | null;
}

const PUBLIC_COMMENT_FIELDS = `
  id
  channelCommentId
  commenterName
  commenterKind
  commentText
  hasReplies
  createdAt
  reactions { reaction count mine }
  ${COMMENT_AVATAR_FIELDS}
  mine
  editable
`;

const PUBLIC_COMMENT_THREAD_QUERY = imageSafeDocs(`
  query PublicCommentThread($url: String!, $entityKind: String, $entityId: ID, $channelCommentId: ID, $visitorId: ID, $page: Int, $pageSize: Int) {
    publicCommentThread(url: $url, entityKind: $entityKind, entityId: $entityId, channelCommentId: $channelCommentId, visitorId: $visitorId, page: $page, pageSize: $pageSize) {
      code
      errors { name errors warnings }
      data { ${PUBLIC_COMMENT_FIELDS} }
    }
  }
`);

// #128/#133 — Load more: a SEPARATE document, so the first page never names the cursor — a site deployed before the
// gateway recomposes keeps every thread readable (only Load more waits for it).
const PUBLIC_COMMENT_THREAD_OLDER_QUERY = imageSafeDocs(`
  query PublicCommentThreadOlder($url: String!, $entityKind: String, $entityId: ID, $channelCommentId: ID, $visitorId: ID, $page: Int, $pageSize: Int, $beforeId: ID!, $before: String) {
    publicCommentThread(url: $url, entityKind: $entityKind, entityId: $entityId, channelCommentId: $channelCommentId, visitorId: $visitorId, page: $page, pageSize: $pageSize, beforeId: $beforeId, before: $before) {
      code
      errors { name errors warnings }
      data { ${PUBLIC_COMMENT_FIELDS} }
    }
  }
`);

const PUBLIC_COMMENT_COUNTS_QUERY = gql`
  query PublicCommentCounts($url: String!, $entityKind: String, $visitorId: ID) {
    publicCommentCounts(url: $url, entityKind: $entityKind, visitorId: $visitorId) {
      code
      errors { name errors warnings }
      data { entityKind entityId count lastCommentAt }
    }
  }
`;

const PUBLIC_COMMENT_REACT_MUTATION = gql`
  mutation PublicCommentReact($url: String!, $entityKind: String, $entityId: ID, $visitorId: ID, $channelCommentId: ID!, $reaction: String!) {
    publicCommentReact(url: $url, entityKind: $entityKind, entityId: $entityId, visitorId: $visitorId, channelCommentId: $channelCommentId, reaction: $reaction) {
      code
      errors { name errors warnings }
      data { id reactions { reaction count mine } }
    }
  }
`;

const PUBLIC_COMMENT_EDIT_MUTATION = imageSafeDocs(`
  mutation PublicCommentEdit($url: String!, $entityKind: String, $entityId: ID, $visitorId: ID, $channelCommentId: ID!, $commentText: String!) {
    publicCommentEdit(url: $url, entityKind: $entityKind, entityId: $entityId, visitorId: $visitorId, channelCommentId: $channelCommentId, commentText: $commentText) {
      code
      errors { name errors warnings }
      data { ${PUBLIC_COMMENT_FIELDS} }
    }
  }
`);

const PUBLIC_COMMENT_REMOVE_MUTATION = gql`
  mutation PublicCommentRemove($url: String!, $entityKind: String, $entityId: ID, $visitorId: ID, $channelCommentId: ID!) {
    publicCommentRemove(url: $url, entityKind: $entityKind, entityId: $entityId, visitorId: $visitorId, channelCommentId: $channelCommentId) {
      code
      errors { name errors warnings }
      data { id }
    }
  }
`;

const PUBLIC_COMMENT_POST_MUTATION = imageSafeDocs(`
  mutation PublicCommentPost(
    $url: String!
    $entityKind: String
    $entityId: ID
    $visitorId: ID
    $commentText: String!
    $channelCommentId: ID
    $commenterName: String
    $sinceCreatedAt: String
  ) {
    publicCommentPost(
      url: $url
      entityKind: $entityKind
      entityId: $entityId
      visitorId: $visitorId
      commentText: $commentText
      channelCommentId: $channelCommentId
      commenterName: $commenterName
      sinceCreatedAt: $sinceCreatedAt
    ) {
      code
      errors { name errors warnings }
      data { ${PUBLIC_COMMENT_FIELDS} }
    }
  }
`);

/** Per-entity comment activity (guest-visible) for the Discuss pill. */
export interface PublicCommentCount {
  entityKind?: string | null;
  entityId?: string | null;
  count?: number | null;
  /** ISO timestamp of the newest guest-visible comment. */
  lastCommentAt?: string | null;
}

export interface PublicCommentThreadArgs {
  url: string;
  entityKind?: string | null;
  entityId?: string | null;
  /** A parent comment id to fetch its replies; omit for the top-level thread. */
  channelCommentId?: string | null;
  /** The current visitor — flags which reactions are theirs. */
  visitorId?: string | null;
  /** 1-based page (newest first). */
  page?: number | null;
  pageSize?: number | null;
  /**
   * #128/#133 — Load more: the oldest comment shown (`olderCursor` → beforeId + its createdAt `before`); the read is the
   * page older than it. Sent as its OWN document, so the first page never depends on the arguments.
   */
  beforeId?: string | null;
  before?: string | null;
}

export interface PublicCommentReactArgs {
  url: string;
  entityKind?: string | null;
  entityId?: string | null;
  visitorId?: string | null;
  /** The comment (or reply) being reacted to. */
  channelCommentId: string;
  /** The emoji to toggle. */
  reaction: string;
}

export interface PublicCommentPostArgs {
  url: string;
  entityKind?: string | null;
  entityId?: string | null;
  visitorId?: string | null;
  commentText: string;
  /** Set to reply to a comment. */
  channelCommentId?: string | null;
  commenterName?: string | null;
  /** Top-level only: the client's newest known comment createdAt — returns the delta since then to merge. */
  sinceCreatedAt?: string | null;
}

export class PublicCommentService {
  /** Read a public comment thread (top-level, or a comment's replies). */
  static async thread(args: PublicCommentThreadArgs, options: Record<string, any> = {}): Promise<OpResult> {
    return executeQueryImageSafe(
      args.beforeId ? PUBLIC_COMMENT_THREAD_OLDER_QUERY : PUBLIC_COMMENT_THREAD_QUERY,
      {
        url: args.url,
        entityKind: args.entityKind ?? null,
        entityId: args.entityId ?? null,
        channelCommentId: args.channelCommentId ?? null,
        visitorId: args.visitorId ?? null,
        page: args.page ?? null,
        pageSize: args.pageSize ?? null,
        ...(args.beforeId ? { beforeId: args.beforeId, before: args.before ?? null } : {}),
      },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** Per-entity comment counts + newest-by-others time for an event. Pass visitorId so the caller's own
   *  posts aren't counted as "new". */
  static async counts(url: string, entityKind?: string | null, visitorId?: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_COMMENT_COUNTS_QUERY,
      { url, entityKind: entityKind ?? null, visitorId: visitorId ?? null },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** Toggle a reaction (👍 etc.) on a comment as a recognized guest. Returns the comment's fresh summary. */
  static async react(args: PublicCommentReactArgs, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(
      PUBLIC_COMMENT_REACT_MUTATION,
      {
        url: args.url,
        entityKind: args.entityKind ?? null,
        entityId: args.entityId ?? null,
        visitorId: args.visitorId ?? null,
        channelCommentId: args.channelCommentId,
        reaction: args.reaction,
      },
      options
    );
  }

  /** Edit your own comment's text — only while it's the newest comment in the thread (no one posted after). */
  static async edit(
    args: { url: string; entityKind?: string | null; entityId?: string | null; visitorId?: string | null; channelCommentId: string; commentText: string },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return executeMutationImageSafe(
      PUBLIC_COMMENT_EDIT_MUTATION,
      {
        url: args.url,
        entityKind: args.entityKind ?? null,
        entityId: args.entityId ?? null,
        visitorId: args.visitorId ?? null,
        channelCommentId: args.channelCommentId,
        commentText: args.commentText,
      },
      options
    );
  }

  /** Remove your own comment (while nobody has replied to it). */
  static async remove(
    args: { url: string; entityKind?: string | null; entityId?: string | null; visitorId?: string | null; channelCommentId: string },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeMutation(
      PUBLIC_COMMENT_REMOVE_MUTATION,
      {
        url: args.url,
        entityKind: args.entityKind ?? null,
        entityId: args.entityId ?? null,
        visitorId: args.visitorId ?? null,
        channelCommentId: args.channelCommentId,
      },
      options
    );
  }

  /** Post a comment or reply as a recognized guest (must have RSVP'd on this device). */
  static async post(args: PublicCommentPostArgs, options: Record<string, any> = {}): Promise<OpResult> {
    return executeMutationImageSafe(
      PUBLIC_COMMENT_POST_MUTATION,
      {
        url: args.url,
        entityKind: args.entityKind ?? null,
        entityId: args.entityId ?? null,
        visitorId: args.visitorId ?? null,
        commentText: args.commentText,
        channelCommentId: args.channelCommentId ?? null,
        commenterName: args.commenterName ?? null,
        sinceCreatedAt: args.sinceCreatedAt ?? null,
      },
      options
    );
  }
}
